import { useEffect, useState } from 'react'
import { useImagePixels } from './useImagePixels'
import { Caption, COLORS } from './ui'

const G = 16
const CHANNELS = [
  { name: 'Red', color: '#ef4444', idx: 0 },
  { name: 'Green', color: '#22c55e', idx: 1 },
  { name: 'Blue', color: '#3b82f6', idx: 2 },
]

// One image = three stacked channel matrices. Faux-3D offset layers convey the
// "depth" of the tensor without a real 3D engine.
export function Tensor2D() {
  const pixels = useImagePixels('/cv/ref-cat.png', G, G)
  const [mobile, setMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  const matrixSize = mobile ? 200 : 132
  const cardSize = matrixSize + 18

  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto',
      alignItems: 'center', justifyContent: 'center', gap: 10, padding: mobile ? '80px 20px 90px' : '90px 24px 110px',
      fontFamily: "'General Sans', system-ui, sans-serif", textAlign: 'center', color: COLORS.text,
    }}>
      <div style={{ fontSize: 'clamp(22px,3vw,34px)', fontWeight: 800, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>224 × 224 × 3</div>
      <div style={{ fontSize: 12, letterSpacing: '0.12em', color: COLORS.indigoLight, fontFamily: "'JetBrains Mono', ui-monospace, monospace", marginBottom: 18 }}>
        IMAGE TENSOR — THREE STACKED CHANNELS
      </div>

      <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', gap: mobile ? 28 : 56, flexWrap: 'wrap', justifyContent: 'center', width: mobile ? '100%' : undefined }}>
        {CHANNELS.map(ch => (
          <div key={ch.name} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <div style={{ position: 'relative', width: cardSize, height: cardSize }}>
              {/* depth shadows */}
              {[10, 5].map((o, k) => (
                <div key={k} style={{
                  position: 'absolute', left: o, top: o, width: matrixSize, height: matrixSize,
                  borderRadius: 6, border: `1px solid ${ch.color}44`, background: `${ch.color}11`,
                }} />
              ))}
              {/* channel matrix */}
              <div style={{
                position: 'absolute', left: 0, top: 0, width: matrixSize, height: matrixSize,
                display: 'grid', gridTemplateColumns: `repeat(${G}, 1fr)`, gap: 1,
                borderRadius: 6, overflow: 'hidden', boxShadow: `0 0 24px ${ch.color}33`,
              }}>
                {(pixels || []).map((p, i) => {
                  const v = p[ch.idx]
                  return <div key={i} style={{ background: `rgb(${ch.idx === 0 ? v : 0},${ch.idx === 1 ? v : 0},${ch.idx === 2 ? v : 0})` }} />
                })}
              </div>
            </div>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: ch.color }}>{ch.name} channel</div>
            <div style={{ fontSize: 12.5, fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: COLORS.textFaint }}>224 × 224 matrix</div>
          </div>
        ))}
      </div>

      <Caption>
        Colour images carry three channels. Stack them and you get a 3D tensor — the universal shape every
        vision model consumes (often as 3 × 224 × 224 in PyTorch’s channels-first layout).
      </Caption>
    </div>
  )
}
